// src/pages/seeker/SeekerJobDetail.jsx
// Read-only job ad — exact visual replica of the employer JobAdMockup in PREVIEW mode.

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { jobAdPublicAPI, seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { DocH1, usePersistedState } from '../../components/shared/UI';
import {
  ArrowLeft, ChevronRight, Share2, Printer, QrCode, Mail, Phone, ExternalLink, X,
} from 'lucide-react';

// ── Palette (identical to employer mockup) ────────────────
const C = {
  blue:         '#3D80CC',
  navy:         '#0e4e95',
  teal:         '#0D4E69',
  maroon:       '#990000',
  adTitle:      '#0066CC',
  posRowBorder: '#0066CC',
  posRowBg:     '#E6E6E6',
  sidebarBg:    '#f5f5f5',
  closingBg:    '#D9EDF7',
  white:        '#ffffff',
  docBorder:    '#B3B3B3',
};

// ── Label maps (identical to employer mockup) ─────────────
const SCHEDULE_LABEL = {
  FULL_TIME: 'Full Time', PART_TIME: 'Part Time', CASUAL: 'Casual', CONTRACT: 'Contract',
};
const PREF_AVAIL_LABEL = {
  URGENT: 'Urgent', ASAP: 'ASAP', PRIORITY: 'Priority', STANDARD: 'Standard', NEGOTIABLE: 'Negotiable',
};
const ADMIN_LEVEL_LABEL = {
  UNSPECIFIED: 'Unspecified', VOLUNTEER: 'Volunteer', OFFICER: 'Officer',
  SUPERVISOR: 'Supervisor', ASSISTANT_MANAGER: 'Assistant Manager',
  MANAGER: 'Manager', SENIOR_MANAGER: 'Senior Manager', EXECUTIVE_MANAGER: 'Executive Manager',
};
const ENVIRONMENT_LABEL = {
  OFFICE: 'Office', TRANSPORTATION: 'Transportation', EDUCATIONAL: 'Educational',
  RETAIL: 'Retail', HOSPITALITY: 'Hospitality / Hotel', MANUFACTURING: 'Manufacturing',
  UNIVERSITY: 'University / School', GOVERNMENT: 'Government', CONSTRUCTION: 'Construction',
  OUTDOORS: 'Outdoors', HEALTHCARE: 'Healthcare', FARMING: 'Farming / Agriculture', OTHER: 'Other',
};
const SALARY_PERIOD_LABEL = { HOURLY: '/hr', DAILY: '/day', WEEKLY: '/wk', ANNUAL: '/yr' };
const EXP_YEAR_LABEL = {
  '1': '1+ years', '2': '2+ years', '3': '3+ years',
  '5': '5+ years', '7': '7+ years', '10': '10+ years',
};
const PROFICIENCY_LABEL = {
  native: 'Native', fluent: 'Fluent', advanced: 'Advanced', professional: 'Professional',
  intermediate: 'Intermediate', conversational: 'Conversational', basic: 'Basic',
};
const IDEAL_SKILL_SUBCATEGORIES = [
  { key: 'general',  label: 'General Skills'                   },
  { key: 'soft',     label: 'Soft / People Skills'             },
  { key: 'software', label: 'Software & Application Knowledge' },
  { key: 'hardware', label: 'Hardware & Equipment Knowledge'   },
];

// ── Shared layout primitives (exact match to mockup) ─────

const SectionBar = ({ children }) => (
  <div style={{
    background: C.blue, color: 'white',
    fontSize: 14, fontWeight: 500, padding: '6px 14px',
  }}>{children}</div>
);

const SidebarBar = ({ children }) => (
  <div style={{
    background: C.blue, color: 'white',
    fontSize: 11, fontWeight: 600, padding: '5px 10px', letterSpacing: '0.04em',
  }}>{children}</div>
);

const SidebarPlaceholder = ({ children, minHeight = 70 }) => (
  <div style={{
    margin: '6px 8px', padding: 8, fontSize: 11,
    color: '#666', fontStyle: 'italic', borderRadius: 2,
    minHeight, lineHeight: 1.6,
  }}>{children}</div>
);

// Requirement label — bold "(Required)" or italic "(Preferred)"
const ReqLabel = ({ req }) => {
  if (!req) return null;
  return req === 'REQUIRED'
    ? <strong style={{ fontSize: 12 }}> (Required)</strong>
    : <span style={{ fontSize: 12, fontStyle: 'italic', fontWeight: 'normal' }}> (Preferred)</span>;
};

// Sub-section heading using DocH1
const SectionLabel = ({ children }) => (
  <DocH1 color={C.teal} style={{ marginTop: 16, marginBottom: 6 }}>{children}</DocH1>
);

// SL — section label row (used within position body)
const SL = ({ children }) => (
  <div style={{ marginTop: 16 }}>
    <DocH1 color={C.teal} style={{ marginBottom: 6 }}>{children}</DocH1>
  </div>
);

// ── Collapsible position sub-section ─────────────────────
const PosSubSection = ({ title, posId, subKey, children }) => {
  const [open, setOpen] = usePersistedState(`tikhet:seeker:jd:${posId}:${subKey}`, true);
  return (
    <div style={{ marginTop: 8 }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '5px 10px', cursor: 'pointer', userSelect: 'none',
          background: '#EEF4FA', borderTop: '1px solid #D0E2F2',
          borderBottom: open ? '1px solid #D0E2F2' : 'none',
        }}
      >
        <ChevronRight size={13} color={C.teal}
          style={{ flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'none' }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: C.teal,
          textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 }}>{title}</span>
      </div>
      {open && <div>{children}</div>}
    </div>
  );
};

// ── QR modal ─────────────────────────────────────────────
const QrModal = ({ onClose }) => ReactDOM.createPortal(
  <div className="modal-overlay" style={{ zIndex: 1100 }}>
    <div className="modal-box" style={{ maxWidth: 340, width: '90vw' }}>
      <div className="modal-header">
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>QR Code</h3>
        <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: '4px 6px' }}>
          <X size={15} />
        </button>
      </div>
      <div className="modal-body" style={{ padding: '32px 24px', textAlign: 'center' }}>
        <QrCode size={52} color={C.blue} style={{ margin: '0 auto 14px', display: 'block' }} />
        <p style={{ color: '#555', fontSize: 14, margin: 0 }}>QR code generation is coming soon.</p>
      </div>
    </div>
  </div>,
  document.body
);

// ── Position row (PREVIEW mode only — no edit/sync) ───────
const PositionRow = ({ position, adId }) => {
  const [expanded, setExpanded] = usePersistedState(
    `tikhet:seeker:jd:${adId}:pos:${position.id}:open`, true
  );
  const [applying, setApplying] = useState(false);
  const [applied, setApplied]   = useState(false);

  const handleApply = async (e) => {
    e.stopPropagation();
    setApplying(true);
    try {
      const { data } = await seekerAPI.applyToPosition(position.id);
      const result = data.data || {};
      toast.success(`Applied! Match: ${result.totalScore ?? 0}% (${result.matchBand || '—'})`);
      setApplied(true);
    } catch {
      // axios interceptor handles toast
    } finally {
      setApplying(false);
    }
  };

  const jd           = position.jobDefinition || {};
  const name         = jd.name || 'Position';
  const wp           = position.workplace || jd.workplace || null;
  const schedule     = position.schedule || jd.schedule;
  const rawSeniority = position.seniorityLevel || jd.seniorityLevel;
  const adminLevel   = rawSeniority && rawSeniority !== 'UNSPECIFIED'
    ? ADMIN_LEVEL_LABEL[rawSeniority] : null;
  const hasSalary    = position.salaryMin != null || position.salaryMax != null;
  const salaryStr    = hasSalary ? [
    position.salaryCurrency || 'AUD',
    position.salaryMin  != null ? position.salaryMin.toLocaleString()  : null,
    position.salaryMax  != null ? `– ${position.salaryMax.toLocaleString()}` : null,
    position.salaryPeriod ? SALARY_PERIOD_LABEL[position.salaryPeriod] : null,
  ].filter(Boolean).join(' ') : null;
  const benefits    = Array.isArray(position.benefits) ? position.benefits : [];
  const roles       = Array.isArray(position.roles) && position.roles.length
    ? position.roles
    : (Array.isArray(jd.roles) ? jd.roles : []);
  const ic          = (position.idealCandidate && typeof position.idealCandidate === 'object')
    ? position.idealCandidate
    : (jd.idealCandidate && typeof jd.idealCandidate === 'object') ? jd.idealCandidate : {};
  const icLangs     = Array.isArray(ic.languages)  ? ic.languages  : [];
  const icEdu       = Array.isArray(ic.education)  ? ic.education  : [];
  const icExp       = Array.isArray(ic.experience) ? ic.experience : [];
  const icSkillsObj = (ic.skills && !Array.isArray(ic.skills)) ? ic.skills : {};
  const customCats  = Array.isArray(ic.customSkillCategories) ? ic.customSkillCategories : [];
  const icAttrs     = Array.isArray(ic.attributes) ? ic.attributes : [];
  const hasIdealData = icLangs.length > 0 || icEdu.length > 0 || icExp.length > 0 || icAttrs.length > 0 ||
    [...IDEAL_SKILL_SUBCATEGORIES, ...customCats.map(c => ({ key: c.key }))].some(
      cat => (icSkillsObj[cat.key] || []).length > 0
    );

  const envLabel    = wp?.environment ? (ENVIRONMENT_LABEL[wp.environment] || wp.environment) : null;
  const languages   = Array.isArray(wp?.workingLanguages) ? wp.workingLanguages : [];
  const addr        = wp?.address && typeof wp.address === 'object' ? wp.address : null;
  const locationStr = [addr?.suburb, addr?.state].filter(Boolean).join(', ');

  const circLabelStyle = { color: C.teal, fontWeight: 600 };

  return (
    <div style={{ marginBottom: 4 }}>
      {/* Name row */}
      <div
        onClick={() => setExpanded(v => !v)}
        style={{
          background: C.posRowBg,
          border: `0.5px solid ${C.posRowBorder}40`,
          borderLeft: `3px solid ${C.posRowBorder}`,
          padding: '7px 12px',
          display: 'flex', alignItems: 'center', gap: 8,
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 14, color: C.maroon, flex: 1 }}>{name}</span>
        <button
          className={`btn ${applied ? 'btn-success' : 'btn-primary'}`}
          style={{ padding: '3px 14px', fontSize: 11, lineHeight: 1.4, minWidth: 70 }}
          onClick={handleApply}
          disabled={applying || applied}
        >
          {applying ? 'Applying...' : applied ? 'Applied' : 'Apply'}
        </button>
        <ChevronRight
          size={16} color={C.adTitle}
          style={{ flexShrink: 0, transition: 'transform 0.2s', transform: expanded ? 'rotate(90deg)' : 'none' }}
        />
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ borderLeft: `3px solid ${C.posRowBorder}20`, paddingBottom: 10 }}>

          {/* ── Position sub-section ── */}
          <PosSubSection title="Position" posId={position.id} subKey="pos">

            <SL>Details</SL>
            <div style={{ padding: '4px 6px' }}>
              <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                {schedule   && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Schedule: </span>{SCHEDULE_LABEL[schedule] || schedule}</li>}
                {adminLevel && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Admin Level: </span>{adminLevel}</li>}
                {hasSalary  && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Salary: </span>{salaryStr}</li>}
                <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}>
                  <span style={circLabelStyle}>Work Rights Status: </span>
                  {position.openToForeignNationals !== false ? 'Open to citizens and non-citizens' : 'Open to citizens'}
                </li>
                {position.preferredAvailability && (
                  <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}>
                    <span style={circLabelStyle}>Preferred Availability: </span>
                    {PREF_AVAIL_LABEL[position.preferredAvailability] || position.preferredAvailability}
                  </li>
                )}
              </ul>
            </div>

            <SL>Workplace</SL>
            <div style={{ padding: '0 6px' }}>
              {wp ? (
                <>
                  {!position.hideWorkplaceName && (
                    <div className="doc-h2" style={{ color: '#444', marginBottom: 4 }}>{wp.name}</div>
                  )}
                  <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                    {envLabel      && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Environment: </span>{envLabel}</li>}
                    {languages.length > 0 && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Languages: </span>{languages.join(', ')}</li>}
                    {locationStr   && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Location: </span>{locationStr}</li>}
                  </ul>
                </>
              ) : (
                <em style={{ color: '#aaa', fontSize: 12 }}>No workplace assigned.</em>
              )}
            </div>

            <SectionLabel>Roles &amp; Responsibilities</SectionLabel>

            {(jd.description || position.description) && (
              <div style={{ padding: '4px 6px', fontSize: 13, color: '#444', lineHeight: 1.6 }}>
                {position.description || jd.description}
              </div>
            )}

            {roles.length > 0 && (
              <div style={{ marginTop: 4 }}>
                {roles.map((role, ri) => (
                  <div key={role.id || ri} style={{ marginBottom: 10 }}>
                    <div style={{ padding: '0 6px' }}>
                      <div className="doc-h2" style={{ color: '#444', marginBottom: 3 }}>
                        {role.title || `Role ${ri + 1}`}
                      </div>
                      {Array.isArray(role.tasks) && role.tasks.length > 0 && (
                        <ol style={{ margin: 0, paddingLeft: 20 }}>
                          {role.tasks.map((task, ti) => (
                            <li key={task.id || ti} style={{ fontSize: 12, color: '#555', marginBottom: 2, lineHeight: 1.5, listStyle: 'decimal' }}>
                              {task.text}
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {benefits.length > 0 && (
              <>
                <div style={{ marginTop: 16 }}>
                  <DocH1 color={C.teal} style={{ marginBottom: 6 }}>Benefits</DocH1>
                </div>
                <div style={{ padding: '0 6px' }}>
                  <ul style={{ margin: 0, paddingLeft: 16, listStyle: 'disc' }}>
                    {benefits.map((b) => (
                      <li key={b} style={{ fontSize: 12, color: '#444', marginBottom: 2 }}>{b}</li>
                    ))}
                  </ul>
                </div>
              </>
            )}

          </PosSubSection>

          {/* ── Expected Profile of Candidates sub-section ── */}
          <PosSubSection title="Expected Profile of Candidates" posId={position.id} subKey="ideal">

            {/* Languages */}
            {icLangs.length > 0 && (
              <div style={{ padding: '0 6px', marginTop: 8, marginBottom: 10 }}>
                <div className="doc-h2" style={{ color: '#444', marginBottom: 3 }}>Languages</div>
                <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                  {icLangs.map((l, i) => {
                    const langName = typeof l === 'string' ? l : l.name;
                    const prof     = typeof l === 'string' ? null : l.proficiency;
                    const req      = typeof l === 'string' ? null : l.requirement;
                    return (
                      <li key={l.id || i} style={{ fontSize: 12, color: '#555', marginBottom: 2, lineHeight: 1.5 }}>
                        {langName}{prof && PROFICIENCY_LABEL[prof] ? ` — ${PROFICIENCY_LABEL[prof]}` : ''}<ReqLabel req={req} />
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Education */}
            {icEdu.length > 0 && (
              <div style={{ padding: '0 6px', marginTop: 6, marginBottom: 10 }}>
                <div className="doc-h2" style={{ color: '#444', marginBottom: 3 }}>Education</div>
                <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                  {icEdu.map(e => (
                    <li key={e.id} style={{ fontSize: 12, color: '#555', marginBottom: 2, lineHeight: 1.5 }}>
                      {e.minLevel ? `${e.minLevel} in ` : ''}{e.fieldOfStudy}
                      <ReqLabel req={e.requirement} />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Experience */}
            {icExp.length > 0 && (
              <div style={{ padding: '0 6px', marginTop: 6, marginBottom: 10 }}>
                <div className="doc-h2" style={{ color: '#444', marginBottom: 3 }}>Experience</div>
                <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                  {icExp.map(e => (
                    <li key={e.id} style={{ fontSize: 12, color: '#555', marginBottom: 2, lineHeight: 1.5 }}>
                      {e.minYears ? (EXP_YEAR_LABEL[e.minYears] || `${e.minYears}+ years`) : ''}
                      {e.positionName ? ` as ${e.positionName}` : ''}
                      {e.adminLevel && ADMIN_LEVEL_LABEL[e.adminLevel] ? ` — ${ADMIN_LEVEL_LABEL[e.adminLevel]}` : ''}
                      <ReqLabel req={e.requirement} />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Skills & Knowledge */}
            {(() => {
              const allCats = [
                ...IDEAL_SKILL_SUBCATEGORIES,
                ...customCats.map(c => ({ key: c.key, label: c.label })),
              ];
              const visibleCats = allCats.filter(cat => (icSkillsObj[cat.key] || []).length > 0);
              if (!visibleCats.length) return null;
              return (
                <div style={{ marginTop: 6, padding: '0 6px' }}>
                  <div className="doc-h2" style={{ color: '#444', marginBottom: 6 }}>Skills &amp; Knowledge</div>
                  {visibleCats.map((cat) => {
                    const items = icSkillsObj[cat.key] || [];
                    return (
                      <div key={cat.key} style={{ paddingLeft: 8, marginBottom: 8 }}>
                        <div className="doc-h3" style={{ color: C.teal, marginBottom: 3 }}>{cat.label}</div>
                        <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                          {items.map(s => (
                            <li key={s.id} style={{ fontSize: 12, color: '#555', marginBottom: 2, lineHeight: 1.5 }}>
                              {s.name}<ReqLabel req={s.requirement} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Attributes */}
            {icAttrs.length > 0 && (
              <div style={{ padding: '0 6px', marginTop: 8, marginBottom: 10 }}>
                <div className="doc-h2" style={{ color: '#444', marginBottom: 3 }}>Attributes</div>
                <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                  {icAttrs.map((a, i) => {
                    const name = typeof a === 'string' ? a : a.name;
                    const req  = typeof a === 'string' ? null : a.requirement;
                    return (
                      <li key={i} style={{ fontSize: 12, color: '#555', marginBottom: 2, lineHeight: 1.5 }}>
                        {name}<ReqLabel req={req} />
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {!hasIdealData && (
              <div style={{ padding: '8px 6px' }}>
                <em style={{ color: '#aaa', fontSize: 12 }}>No candidate profile defined for this position.</em>
              </div>
            )}

          </PosSubSection>

        </div>
      )}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────
export const SeekerJobDetail = () => {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [ad, setAd]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrModal, setQrModal] = useState(false);

  useEffect(() => {
    jobAdPublicAPI.getById(id)
      .then(({ data }) => setAd(data.data.ad))
      .catch(() => navigate('/seeker/jobs', { replace: true }))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  if (loading) return (
    <div className="fade-in" style={{ padding: 32, color: 'var(--text-muted)' }}>Loading…</div>
  );
  if (!ad) return null;

  const profile     = ad.groupProfile || {};
  const contacts    = Array.isArray(ad.contacts) ? ad.contacts : [];
  const positions   = Array.isArray(ad.positions) ? ad.positions : [];
  const closingDate = ad.closingDate
    ? new Date(ad.closingDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  const adIdDisplay = ad.adNumber != null
    ? String(ad.adNumber).padStart(4, '0')
    : ad.id?.slice(-8).toUpperCase();

  const socialLinks = [
    { label: 'Website',   url: profile.website      },
    { label: 'LinkedIn',  url: profile.linkedinUrl  },
    { label: 'Twitter',   url: profile.twitterUrl   },
    { label: 'Facebook',  url: profile.facebookUrl  },
    { label: 'Instagram', url: profile.instagramUrl },
  ].filter(l => l.url);

  const iconBtn = {
    background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)',
    cursor: 'pointer', padding: '2px 3px', display: 'flex', alignItems: 'center',
    borderRadius: 3, lineHeight: 1,
  };

  return (
    <div className="fade-in">
      {qrModal && <QrModal onClose={() => setQrModal(false)} />}

      {/* Toolbar */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontWeight: 700, fontSize: 15, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {ad.title}
        </span>
      </div>

      {/* Back link — in grey area below titlebar */}
      <div style={{ paddingBottom: 6 }}>
        <button
          onClick={() => navigate('/seeker/jobs')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
            color: 'var(--text-muted)', fontSize: 12, fontWeight: 600,
            padding: '4px 0', fontFamily: 'inherit',
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#3D80CC'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
        >
          <ArrowLeft size={13} /> Browse Jobs
        </button>
      </div>

      {/* Document */}
      <div style={{ padding: '8px 0 16px' }}>
        <div style={{
          borderRadius: 6,
          border: `1px solid ${C.docBorder}`,
          overflow: 'hidden',
          fontFamily: "'DM Sans', sans-serif",
          background: C.white,
          boxShadow: '0 4px 24px rgba(14,78,149,0.12)',
        }}>

          {/* Ad Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '20px 22px', borderBottom: '1px solid #e0e0e0' }}>
            <div style={{
              width: 72, height: 72, flexShrink: 0, borderRadius: 4,
              background: '#ebebeb', display: 'flex', alignItems: 'center',
              justifyContent: 'center', overflow: 'hidden',
            }}>
              {profile.logoUrl
                ? <img src={profile.logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                : <span style={{ fontSize: 11, color: '#bbb', textAlign: 'center' }}>LOGO</span>
              }
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: C.adTitle, lineHeight: 1.2 }}>
                {ad.title}
              </div>
              <div style={{ fontSize: 15, marginTop: 4 }}>
                <span style={{ fontWeight: 700, color: '#444' }}>with </span>
                <span style={{ fontWeight: 700, color: C.maroon }}>{profile.profileName || '—'}</span>
              </div>
            </div>
          </div>

          {/* Two-column body */}
          <div style={{ display: 'flex' }}>

            {/* LEFT SIDEBAR */}
            <div style={{ width: 200, flexShrink: 0, background: C.sidebarBg, borderRight: '1px solid #ddd' }}>

              {/* Ad ID + share icons */}
              <div style={{
                background: C.blue, color: 'white', padding: '7px 10px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.8 }}>ID</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#cde3f7', letterSpacing: '0.08em' }}>
                    {adIdDisplay}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button onClick={() => navigator.clipboard?.writeText(window.location.href)} title="Copy link" style={iconBtn}>
                    <Share2 size={14} />
                  </button>
                  <button onClick={() => window.print()} title="Print" style={iconBtn}>
                    <Printer size={14} />
                  </button>
                  <button onClick={() => setQrModal(true)} title="QR Code" style={iconBtn}>
                    <QrCode size={14} />
                  </button>
                </div>
              </div>

              {/* Closing date */}
              {closingDate && (
                <div style={{ background: C.closingBg, padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: C.teal, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    Closing Date:
                  </span>
                  <span style={{ fontSize: 12, color: '#333' }}>{closingDate}</span>
                </div>
              )}

              {/* Positions Included */}
              {positions.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <SidebarBar>Positions Included</SidebarBar>
                  <div style={{ padding: '4px 0 2px' }}>
                    {positions.map(p => (
                      <div key={p.id} style={{ fontSize: 11, padding: '4px 10px', lineHeight: 1.4, color: '#444' }}>
                        {p.jobDefinition?.name || 'Position'}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Details */}
              <div style={{ marginTop: 8 }}>
                <SidebarBar>Contact Details</SidebarBar>
                {contacts.length > 0 ? (
                  <div>
                    {contacts.map(c => (
                      <div key={c.id} style={{ padding: '7px 10px', borderBottom: '1px solid #eee' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#333' }}>{c.name}</div>
                        {c.emails?.[0] && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.blue, marginTop: 4 }}>
                            <Mail size={11} style={{ flexShrink: 0 }} />
                            {c.emails[0].email || c.emails[0].value}
                          </div>
                        )}
                        {c.phones?.[0] && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#555', marginTop: 2 }}>
                            <Phone size={11} style={{ flexShrink: 0 }} />
                            {c.phones[0].phone || [c.phones[0].code, c.phones[0].number].filter(Boolean).join(' ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <SidebarPlaceholder minHeight={50}>No contacts assigned.</SidebarPlaceholder>
                )}
              </div>

              {/* Social Links */}
              {socialLinks.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <SidebarBar>Social</SidebarBar>
                  <div style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {socialLinks.map(l => (
                      <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11,
                          color: C.blue, textDecoration: 'none', fontWeight: 500 }}
                        onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                      >
                        <ExternalLink size={9} style={{ flexShrink: 0 }} /> {l.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}


            </div>

            {/* RIGHT CONTENT */}
            <div style={{ flex: 1, minWidth: 0, paddingBottom: 20 }}>

              {/* Ad Description */}
              <SectionBar>Ad Description</SectionBar>
              <div style={{ padding: '10px 14px' }}>
                {ad.description ? (
                  <div className="rte-editor" style={{ fontSize: 14, lineHeight: 1.65, color: '#333' }}
                    dangerouslySetInnerHTML={{ __html: ad.description }} />
                ) : (
                  <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>No description.</div>
                )}
              </div>

              {/* Ad Positions */}
              <div style={{ display: 'flex', alignItems: 'center', background: C.blue, color: 'white', fontSize: 14, fontWeight: 500, padding: '6px 14px' }}>
                Ad Positions
              </div>
              <div style={{ padding: '10px 14px' }}>
                {positions.length === 0 ? (
                  <p style={{ fontSize: 13, color: '#aaa', fontStyle: 'italic', margin: 0 }}>
                    No positions added to this ad yet.
                  </p>
                ) : (
                  positions.map(pos => (
                    <PositionRow key={pos.id} position={pos} adId={id} />
                  ))
                )}
              </div>

              {/* How to Apply */}
              <SectionBar>How to Apply</SectionBar>
              <div style={{ padding: '10px 14px' }}>
                {ad.howToApply ? (
                  <div className="rte-editor" style={{ fontSize: 14, lineHeight: 1.65, color: '#333' }}
                    dangerouslySetInnerHTML={{ __html: ad.howToApply }} />
                ) : (
                  <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>No instructions provided.</div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
